import SalviaLayout from "@/components/SalviaLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Download, AlertTriangle, CheckCircle2, AlertCircle, XCircle, ArrowLeft, Printer } from "lucide-react";
import { useLocation, useParams } from "wouter";

interface DimensionResult {
  name: string;
  score: number;
  maxScore: number;
  percentage: number;
  status: "low" | "medium" | "high" | "critical";
}

const mockDimensionResults: DimensionResult[] = [
  { name: "Pressao de Tempo", score: 7, maxScore: 10, percentage: 70, status: "high" },
  { name: "Atencao e Concentracao", score: 8, maxScore: 10, percentage: 80, status: "critical" },
  { name: "Complexidade das Tarefas", score: 6, maxScore: 10, percentage: 60, status: "medium" },
  { name: "Monotonia e Repetitividade", score: 4, maxScore: 10, percentage: 40, status: "low" },
  { name: "Raciocinio e Decisao", score: 7, maxScore: 10, percentage: 70, status: "high" },
  { name: "Iniciativa e Autonomia", score: 5, maxScore: 10, percentage: 50, status: "medium" },
  { name: "Isolamento Social", score: 3, maxScore: 10, percentage: 30, status: "low" },
  { name: "Horarios e Turnos", score: 6, maxScore: 10, percentage: 60, status: "medium" },
  { name: "Relacionamento Interpessoal", score: 4, maxScore: 10, percentage: 40, status: "low" },
  { name: "Demandas Gerais", score: 7, maxScore: 10, percentage: 70, status: "high" },
];

const recommendations = [
  {
    dimension: "Atencao e Concentracao",
    risk: "critical",
    actions: [
      "Implementar pausas programadas a cada 2 horas de trabalho",
      "Reduzir interrupcoes no ambiente de trabalho",
      "Avaliar redistribuicao de tarefas que exigem alta concentracao",
    ],
  },
  {
    dimension: "Pressao de Tempo",
    risk: "high",
    actions: [
      "Revisar prazos e metas estabelecidas",
      "Implementar sistema de priorizacao de tarefas",
      "Avaliar necessidade de contratacao adicional",
    ],
  },
  {
    dimension: "Raciocinio e Decisao",
    risk: "high",
    actions: [
      "Estabelecer protocolos de decisao para situacoes recorrentes",
      "Criar sistema de suporte para decisoes complexas",
      "Implementar treinamentos em gestao de tempo e decisao",
    ],
  },
];

export default function Result() {
  const params = useParams();
  const [, setLocation] = useLocation();

  // Valores calculados (em producao viriam do backend)
  const totalA = 32;
  const totalB = 25;
  const mentalLoadScore = 0.83 * (totalA + totalB);
  const mentalLoadPercentage = (mentalLoadScore / 83) * 100;

  const getQualifier = (score: number) => {
    if (score <= 20) return { label: "Muito Baixa", color: "text-green-600", bg: "bg-green-100" };
    if (score <= 35) return { label: "Baixa", color: "text-green-600", bg: "bg-green-100" };
    if (score <= 50) return { label: "Moderada", color: "text-yellow-600", bg: "bg-yellow-100" };
    if (score <= 65) return { label: "Alta", color: "text-orange-600", bg: "bg-orange-100" };
    return { label: "Muito Alta", color: "text-red-600", bg: "bg-red-100" };
  };

  const getRiskLevel = (probability: number, severity: number) => {
    const riskMatrix = probability * severity;
    if (riskMatrix <= 4) return { level: "Aceitavel", color: "bg-green-500", textColor: "text-green-800", bgLight: "bg-green-100" };
    if (riskMatrix <= 9) return { level: "Nivel de Atencao", color: "bg-yellow-500", textColor: "text-yellow-800", bgLight: "bg-yellow-100" };
    if (riskMatrix <= 16) return { level: "Incerto", color: "bg-orange-500", textColor: "text-orange-800", bgLight: "bg-orange-100" };
    return { level: "Inaceitavel", color: "bg-red-500", textColor: "text-red-800", bgLight: "bg-red-100" };
  };

  const qualifier = getQualifier(mentalLoadScore);
  const probability = 3;
  const severity = 4;
  const riskLevel = getRiskLevel(probability, severity);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "low":
        return <CheckCircle2 className="text-green-500" size={20} />;
      case "medium":
        return <AlertCircle className="text-yellow-500" size={20} />;
      case "high":
        return <AlertTriangle className="text-orange-500" size={20} />;
      case "critical":
        return <XCircle className="text-red-500" size={20} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "low":
        return "bg-green-500";
      case "medium":
        return "bg-yellow-500";
      case "high":
        return "bg-orange-500";
      case "critical":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <SalviaLayout title="Resultado da Avaliacao">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setLocation("/evaluations")}>
            <ArrowLeft size={20} className="mr-2" />
            Voltar
          </Button>
          <div className="flex gap-2">
            <Button variant="outline">
              <Printer size={20} className="mr-2" />
              Imprimir
            </Button>
            <Button className="bg-[#C0FF00] hover:bg-[#a8e600] text-[#002F59]">
              <Download size={20} className="mr-2" />
              Gerar PDF
            </Button>
          </div>
        </div>

        {/* Info Card */}
        <Card className="p-6 bg-[#002F59] text-white">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-300">Cargo</p>
              <p className="text-xl font-bold">Desenvolvedor</p>
            </div>
            <div>
              <p className="text-sm text-gray-300">Setor</p>
              <p className="text-xl font-bold">Tecnologia da Informacao</p>
            </div>
            <div>
              <p className="text-sm text-gray-300">Cliente</p>
              <p className="text-xl font-bold">Tech Solutions LTDA</p>
            </div>
            <div>
              <p className="text-sm text-gray-300">Data da Avaliacao</p>
              <p className="text-xl font-bold">13/01/2026</p>
            </div>
          </div>
        </Card>

        {/* Main Results */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Mental Load Score */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-[#002F59] mb-4">Carga Mental</h3>
            <div className="flex items-center justify-center mb-4">
              <div className="relative w-40 h-40">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke="#e5e7eb"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke="#C0FF00"
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${mentalLoadPercentage * 4.4} 440`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-[#002F59]">{mentalLoadScore.toFixed(1)}</span>
                  <span className="text-sm text-gray-500">pontos</span>
                </div>
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">Formula: 0,83 x (Total A + Total B)</p>
              <p className="text-sm text-gray-600">
                0,83 x ({totalA} + {totalB}) = <span className="font-bold">{mentalLoadScore.toFixed(2)}</span>
              </p>
            </div>
          </Card>

          {/* Qualifier */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-[#002F59] mb-4">Qualificador de Risco</h3>
            <div className={`p-6 rounded-xl ${qualifier.bg} text-center mb-4`}>
              <p className={`text-3xl font-bold ${qualifier.color}`}>{qualifier.label}</p>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Grupo A:</span>
                <span className="font-semibold">{totalA} pontos</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Grupo B:</span>
                <span className="font-semibold">{totalB} pontos</span>
              </div>
            </div>
          </Card>

          {/* Risk Matrix */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-[#002F59] mb-4">Matriz de Risco</h3>
            <div className={`p-6 rounded-xl ${riskLevel.bgLight} text-center mb-4`}>
              <p className={`text-2xl font-bold ${riskLevel.textColor}`}>{riskLevel.level}</p>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Probabilidade:</span>
                <span className="font-semibold">{probability} (Provavel)</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Severidade:</span>
                <span className="font-semibold">{severity} (Alta)</span>
              </div>
              <div className="flex justify-between text-sm border-t pt-2 mt-2">
                <span className="text-gray-600">Indice de Risco:</span>
                <span className="font-bold text-[#002F59]">{probability * severity}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Dimension Results */}
        <Card className="p-6">
          <h3 className="text-lg font-bold text-[#002F59] mb-6">Resultado por Dimensao</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {mockDimensionResults.map((dim) => (
              <div key={dim.name} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                {getStatusIcon(dim.status)}
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{dim.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getStatusColor(dim.status)}`}
                        style={{ width: `${dim.percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-gray-600">{dim.score}/{dim.maxScore}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recommendations */}
        <Card className="p-6">
          <h3 className="text-lg font-bold text-[#002F59] mb-6">Recomendacoes e Plano de Acao</h3>
          <div className="space-y-6">
            {recommendations.map((rec, index) => (
              <div key={index} className={`p-4 rounded-lg border-l-4 ${
                rec.risk === "critical" ? "border-l-red-500 bg-red-50" :
                rec.risk === "high" ? "border-l-orange-500 bg-orange-50" :
                "border-l-yellow-500 bg-yellow-50"
              }`}>
                <div className="flex items-center gap-2 mb-3">
                  {rec.risk === "critical" ? <XCircle className="text-red-500" size={20} /> :
                   rec.risk === "high" ? <AlertTriangle className="text-orange-500" size={20} /> :
                   <AlertCircle className="text-yellow-500" size={20} />}
                  <h4 className="font-bold text-gray-900">{rec.dimension}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    rec.risk === "critical" ? "bg-red-100 text-red-800" :
                    rec.risk === "high" ? "bg-orange-100 text-orange-800" :
                    "bg-yellow-100 text-yellow-800"
                  }`}>
                    {rec.risk === "critical" ? "Critico" : rec.risk === "high" ? "Alto" : "Moderado"}
                  </span>
                </div>
                <ul className="space-y-2">
                  {rec.actions.map((action, actionIndex) => (
                    <li key={actionIndex} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-[#C0FF00] font-bold">•</span>
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Card>

        {/* Generate Document */}
        <Card className="p-6 bg-gradient-to-r from-[#002F59] to-[#004080] text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-lg">
                <FileText size={32} />
              </div>
              <div>
                <h3 className="text-xl font-bold">Inventario de Riscos Psicossociais</h3>
                <p className="text-sm text-gray-300">Documento completo conforme NR-01 e NR-17</p>
              </div>
            </div>
            <Button
              className="bg-[#C0FF00] hover:bg-[#a8e600] text-[#002F59] font-semibold"
              onClick={() => setLocation(`/document/${params.id}`)}
            >
              <FileText size={20} className="mr-2" />
              Gerar Documento
            </Button>
          </div>
        </Card>
      </div>
    </SalviaLayout>
  );
}
